import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// ── JWT decode helper ────────────────────────────────────────────────────────
// Decodes the payload of a JWT token (base64url) without verification.
// Returns the payload object or null if decoding fails.
const decodeJwtPayload = (token: string): { exp?: number } | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // JWT payload is the second part, base64url-encoded
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(payload);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

// ── Proactive token refresh ──────────────────────────────────────────────────
// Refreshes the token BEFORE it expires to prevent 401 errors.
// Returns the current token (new or old) on success, null on failure.
const proactiveRefresh = async (): Promise<string | null> => {
  const token = localStorage.getItem('accessToken');
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return token; // No expiry info — keep the token

  const now = Math.floor(Date.now() / 1000);
  const timeLeft = payload.exp - now;

  // Only refresh if token expires within 2 minutes (120 seconds)
  if (timeLeft > 120) return token;

  console.debug(`[Token] Proactive refresh: ${timeLeft}s remaining`);
  
  // H-06: refresh token lives in an httpOnly cookie (set by the backend on login).
  // We no longer read it from localStorage — the cookie is sent automatically
  // because the axios instance uses withCredentials: true.
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
      withCredentials: true,
    });

    const { accessToken } = response.data?.data || response.data;
    
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
      
      // Update Zustand auth store reactively
      try {
        const authStorage = localStorage.getItem('auth-storage');
        if (authStorage) {
          const parsed = JSON.parse(authStorage);
          parsed.state.token = accessToken;
          parsed.state.isAuthenticated = true;
          localStorage.setItem('auth-storage', JSON.stringify(parsed));
        }
      } catch {
        // Ignore parse errors
      }

      return accessToken;
    }
  } catch {
    // Refresh failed — keep old token, reactive handler will deal with 401
  }
  
  return token;
};

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    const url: string = config.url || '';
    const staffToken = localStorage.getItem('accessToken');
    // Use portal token when on a /portal/* page (customer context) OR when there is no
    // staff token. Staff token always wins on non-portal pages (e.g. admin managing portal users).
    const isPortalPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/portal/');
    if (url.includes('/support/portal/') && (!staffToken || isPortalPage)) {
      const portalToken = localStorage.getItem('portalToken');
      if (portalToken) {
        config.headers.Authorization = `Bearer ${portalToken}`;
        return config;
      }
    }
    if (staffToken) {
      config.headers.Authorization = `Bearer ${staffToken}`;
    }
    // Proactively refresh token before making the request if it's about to expire
    const newToken = await proactiveRefresh();
    if (newToken) {
      if (url.includes('/support/portal/') && (!staffToken || isPortalPage)) {
        // Portal context — don't override with staff token
      } else if (staffToken || newToken) {
        config.headers.Authorization = `Bearer ${newToken}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token refresh mutex to prevent concurrent refresh attempts
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retrying, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't attempt token refresh for auth/login endpoints — 401 there means bad credentials,
      // not an expired token. Let the calling code handle the error directly.
      const url: string = originalRequest.url || '';
      if (url.includes('/auth/user/login') || url.includes('/auth/admin/login') || url.includes('/auth/verify-otp')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // H-06: refresh token is in the httpOnly cookie — sent automatically via withCredentials.
      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
          withCredentials: true,
        });

        const { accessToken } = response.data?.data || response.data;
        
        localStorage.setItem('accessToken', accessToken);

        // Update Zustand auth store reactively
        try {
          const authStorage = localStorage.getItem('auth-storage');
          if (authStorage) {
            const parsed = JSON.parse(authStorage);
            parsed.state.token = accessToken;
            parsed.state.isAuthenticated = true;
            localStorage.setItem('auth-storage', JSON.stringify(parsed));
          }
        } catch {
          // Ignore parse errors
        }

        // Also update the Zustand store in-memory if possible
        try {
          const { useAuthStore } = await import('../stores/authStore');
          useAuthStore.setState({ token: accessToken, isAuthenticated: true });
          } catch {
            // Ignore if store not available
          }

          isRefreshing = false;
          processQueue(null, accessToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(refreshError, null);
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('accessToken');
          // Clear Zustand auth state to prevent ProtectedRoute from keeping user in
          try {
            const authStorage = localStorage.getItem('auth-storage');
            if (authStorage) {
              const parsed = JSON.parse(authStorage);
              parsed.state.token = null;
              parsed.state.isAuthenticated = false;
              parsed.state.user = null;
              localStorage.setItem('auth-storage', JSON.stringify(parsed));
            }
          } catch { /* ignore */ }
          try {
            const { useAuthStore } = await import('../stores/authStore');
            useAuthStore.setState({ token: null, isAuthenticated: false, user: null });
          } catch { /* ignore */ }
          window.location.href = '/login';
        }
    }

    return Promise.reject(error);
  }
);

export default api;

// Helper functions
export const extractData = <T>(response: any): T => {
  return response.data?.data?.data || response.data?.data || response.data;
};

export const handleApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};
